<template>
  <div>
    <div class="mb-4">
      <button
        type="button"
        class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        @click="goBackToUpsells"
      >
        <svg
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          class="-ml-1 mr-2 h-5 w-5"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M10 19l-7-7m0 0l7-7m-7 7h18"
          ></path>
        </svg>
        Back to Upsells
      </button>
    </div>

    <div v-if="upsell" class="mb-5">
      <div class="flex items-center gap-3">
        <h2 class="text-2xl font-semibold text-gray-900">{{ upsell.name }}</h2>
        <span class="Polaris-Badge" :class="getUpsellBadgeTone(upsell.status)">
          <span class="Polaris-Text--root Polaris-Text--visuallyHidden">
            {{ getUpsellBadgeA11yTone(upsell.status) }}
          </span>
          <span class="Polaris-Text--root Polaris-Text--bodySm">
            {{ getUpsellStatus(upsell.status) }}
          </span>
        </span>
      </div>

      <div class="mt-4 bg-white overflow-hidden shadow rounded-lg">
        <div class="px-4 py-5 sm:p-6">
          <h4 class="text-lg leading-6 font-medium text-gray-900 mb-4">
            Upsell details
          </h4>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p class="text-xs text-gray-500 uppercase">Upsell name</p>
              <p class="text-sm text-gray-900">{{ upsell.name }}</p>
            </div>
            <div>
              <p class="text-xs text-gray-500 uppercase">Status</p>
              <p class="text-sm text-gray-900">
                {{ getUpsellStatus(upsell.status) }}
              </p>
            </div>
            <div>
              <p class="text-xs text-gray-500 uppercase">Created at</p>
              <p class="text-sm text-gray-900">
                {{ formatTimestamp(upsell.createdAt) }}
              </p>
            </div>
            <div>
              <p class="text-xs text-gray-500 uppercase">Send after</p>
              <p class="text-sm text-gray-900">
                {{ upsell.sendAfterDays }} days after
                {{ (upsell.sendAfter || "").replace(/_/g, " ") }}
              </p>
            </div>
            <div>
              <p class="text-xs text-gray-500 uppercase">Applies to</p>
              <p class="text-sm text-gray-900">{{ upsell.appliesTo || "-" }}</p>
            </div>
            <div>
              <p class="text-xs text-gray-500 uppercase">Products</p>
              <p class="text-sm text-gray-900">{{ selectedProductsLabel }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <h3 class="text-lg leading-6 font-medium text-gray-900 mt-5">
      Upsell Stats
    </h3>
    <h3 class="text-base leading-6 font-medium text-gray-900 my-2">
      Select Date
    </h3>
    <DateRangePicker
      v-model="currentConfig"
      opens="right"
      :ranges="ranges"
      @update="dateSelected"
    />
    <h3 class="text-lg leading-6 font-medium text-gray-900 mt-4">
      {{ dateDiff }}
    </h3>
    <div class="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3">
      <div class="bg-white overflow-hidden shadow rounded-lg">
        <div class="px-4 py-5 sm:p-6">
          <dl>
            <dt class="text-sm leading-5 font-medium text-gray-500 truncate">
              Total Sent
            </dt>
            <dd class="mt-1 text-3xl leading-9 font-semibold text-gray-900">
              {{ report.totalSent }}
            </dd>
          </dl>
        </div>
      </div>

      <div class="bg-white overflow-hidden shadow rounded-lg relative">
        <div class="px-4 py-5 sm:p-6">
          <dl>
            <dt
              class="text-sm leading-5 font-medium text-gray-500 truncate flex items-center"
            >
              Orders Generated
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4 text-gray-500 hover:text-gray-700 cursor-pointer ml-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                @mouseenter="showPopover = true"
                @mouseleave="showPopover = false"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              <div
                v-if="showPopover"
                class="absolute z-10 px-2 py-1 -mt-1 text-sm bg-gray-700 text-white rounded shadow-lg"
                style="white-space: break-spaces"
              >
                Orders placed by customers within 7 days of receiving the
                campaign message
              </div>
            </dt>
            <dd class="mt-1 text-3xl leading-9 font-semibold text-gray-900">
              <span v-if="isLoading">
                <Spinner />
              </span>
              <span v-else>
                {{ orderStats.totalOrders }}
              </span>
            </dd>
          </dl>
        </div>
      </div>
      <div class="bg-white overflow-hidden shadow rounded-lg">
        <div class="px-4 py-5 sm:p-6">
          <dl>
            <dt class="text-sm leading-5 font-medium text-gray-500 truncate">
              Total Order value from the upsell
            </dt>
            <dd class="mt-1 text-3xl leading-9 font-semibold text-gray-900">
              <span v-if="isLoading">
                <Spinner />
              </span>
              <span v-else> {{ generatedValue }} </span>
            </dd>
          </dl>
        </div>
      </div>
    </div>

    <div
      class="mt-5 bg-white overflow-hidden shadow rounded-lg"
      v-if="template"
    >
      <div class="px-4 py-5 sm:p-6">
        <h4 class="text-lg leading-6 font-medium text-gray-900">Message</h4>
        <p class="mt-2 text-sm text-gray-600">{{ template.message }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import Spinner from "vue-simple-spinner";
import DateRangePicker from "vue2-daterange-picker";
import "vue2-daterange-picker/dist/vue2-daterange-picker.css";

import {
  subDays,
  format,
  endOfDay,
  startOfDay,
  startOfMonth,
  endOfMonth
} from "date-fns";

export default {
  components: {
    Spinner,
    DateRangePicker
  },
  data() {
    return {
      report: {},
      template: null,
      upsell: null,
      orders: [],
      orderStats: {},
      showPopover: false,
      totalOrderValue: 0,
      isLoading: false,
      currencyFormat: "{{amount}}",
      currentConfig: {
        startDate: "",
        endDate: ""
      }
    };
  },
  computed: {
    dateDiff() {
      const startDate = this.currentConfig.startDate;
      const endDate = this.currentConfig.endDate;

      if (startDate && endDate) {
        return `${format(startDate, "dd MMM yyyy")} - ${format(
          endDate,
          "dd MMM yyyy"
        )}`;
      }
      return "";
    },
    generatedValue() {
      const value = this.currencyFormat.replace(
        "{{amount}}",
        this.orderStats.totalRevenue
      );
      return value;
    },
    selectedProductsLabel() {
      const products = this.upsell?.conditions?.products || [];
      if (!products.length) return "-";
      return products.map((product) => product.title).join(", ");
    }
  },
  created() {
    let today = new Date();
    today.setHours(0, 0, 0, 0);

    let yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    yesterday.setHours(0, 0, 0, 0);
    this.ranges = {
      Today: [today, today],
      Yesterday: [yesterday, yesterday],
      "This month": [startOfMonth(today), endOfMonth(today)],

      "Last month": [
        new Date(today.getFullYear(), today.getMonth() - 1, 1),
        new Date(today.getFullYear(), today.getMonth(), 0)
      ]
    };

    // this.getOrderReport();
  },
  mounted() {
    const startDate = subDays(new Date(), 7);
    this.currentConfig = {
      startDate: startDate,
      endDate: new Date()
    };
    this.getReport();
  },
  methods: {
    goBackToUpsells() {
      this.$router.push({ name: "Upsells" });
    },
    formatTimestamp(value) {
      if (!value) return "-";
      return format(new Date(value * 1000), "h:mm a, MMM dd");
    },
    getUpsellStatus(status) {
      if (status === "waiting_for_approval") {
        return "Waiting for approval";
      }
      if (!status) return "-";
      return status.replace(/_/g, " ");
    },
    getUpsellBadgeTone(status) {
      if (status === "waiting_for_approval") {
        return "Polaris-Badge--toneWarning";
      }
      if (status === "active" || status === "scheduled") {
        return "Polaris-Badge--toneSuccess";
      }
      if (status === "inactive" || status === "draft") {
        return "Polaris-Badge--toneInfo";
      }
      return "";
    },
    getUpsellBadgeA11yTone(status) {
      if (status === "waiting_for_approval") return "Warning";
      if (status === "active" || status === "scheduled") return "Success";
      if (status === "inactive" || status === "draft") return "Info";
      return "Status";
    },
    dateSelected() {
      this.getReport();
    },
    async getReport() {
      this.isLoading = true;
      const startDate =
        startOfDay(this.currentConfig.startDate).getTime() / 1000;
      const endDate = endOfDay(this.currentConfig.endDate).getTime() / 1000;

      const upsellId = this.$route.params.upsellId;
      this.$store
        .dispatch("Upsell/getReport", { upsellId, startDate, endDate })
        .then((response) => {
          this.report = response.report;
          this.upsell = response.upsell;
          this.template = response.template || this.template;
          return this.$store.dispatch("Upsell/getOrderReport", {
            upsellId,
            startDate,
            endDate
          });
        })
        .then((response) => {
          this.orderStats = response.report;
          this.currencyFormat = response.currencyFormat;
          this.isLoading = false;
        });
    },
    getOrderReport() {
      this.isLoading = true;
      const upsellId = this.$route.params.upsellId;
      this.$store
        .dispatch("Upsell/getOrderStats", { upsellId })
        .then((response) => {
          this.isLoading = false;
          console.log("response", response);
          this.orders = response.orderStats.orderNumbers;
          if (response.orderStats.orderNumbers.length > 0) {
            this.totalOrderValue = response.orderStats.totalPrice;
          }
        });
    }
  }
};
</script>

<style lang="scss" scoped></style>
